/**
 * @license Copyright (c) 2023-2025, CKSource Holding sp. z o.o. All rights reserved.
 * For licensing, see LICENSE.md.
 */

import { CheckSquareFilled, MinusSquareFilled } from '@ant-design/icons';
import { Box, type PaletteColorOptions } from '@mui/material';
import { createTheme as createMuiTheme } from '@mui/material/styles';
import type { ColorPartial } from '@mui/material/styles/createPalette';
import type { Theme } from '@mui/material/styles/createTheme';

declare module '@mui/material/styles' {
	interface PaletteColor extends Omit<ColorPartial, 'contrastText'> {}
	interface PaletteOptions {
		blue: PaletteColorOptions;
		cyan: PaletteColorOptions;
		green: PaletteColorOptions;
		orange: PaletteColorOptions;
		rose: PaletteColorOptions;
	}
	interface TypeText {
		contrast: string;
	}
}

export const createTheme = (): Theme => {
	const theme = createMuiTheme( {
		palette: {
			mode: 'light',
			primary: {
				50: '#F5F7FF',
				100: '#F0EBF8',
				200: '#E8DFF7',
				light: '#C4B1E6',
				400: '#9C80D1',
				main: '#743CCD',
				600: '#582AA2',
				dark: '#391B69',
				800: '#160A29'
			},
			secondary: {
				100: '#E3E8ED',
				200: '#C3D0DE',
				light: '#BCC7CE',
				400: '#A1B0C2',
				main: '#7E91A7',
				600: '#607892',
				dark: '#3B4958',
				800: '#293440'
			},
			error: {
				50: '#FFF3F5',
				100: '#FFCBD0',
				200: '#FA9594',
				light: '#F36A6A',
				400: '#FE4343',
				main: '#E3001D',
				600: '#D60016',
				dark: '#C60005'
			},
			warning: {
				100: '#FFF7EA',
				200: '#FFDC96',
				light: '#FFCC68',
				400: '#E7A138',
				main: '#E29849',
				600: '#D27E3F',
				dark: '#A95F11'
			},
			info: {
				100: '#ECEEFF',
				200: '#D7D9FE',
				light: '#C0C3F2',
				400: '#949CE9',
				main: '#745FB9',
				600: '#5B45AD',
				dark: '#45389E'
			},
			success: {
				100: '#E7F6EB',
				200: '#C6E8CD',
				light: '#A1D9AE',
				400: '#5CC075',
				main: '#35A553',
				600: '#21823C',
				dark: '#277E3E'
			},
			grey: {
				100: '#FFFFFF',
				200: '#F8F9FA',
				300: '#F1F2F3',
				400: '#E8E9EA',
				500: '#D9DADB',
				600: '#B5B6B7',
				700: '#969797',
				800: '#6D6E6F',
				900: '#3B3C3D'
			},
			text: {
				primary: '#001234',
				secondary: '#585B5F',
				disabled: '#A1B0C2',
				contrast: '#FFFFFF'
			},
			action: { disabledOpacity: 0.5 },
			common: {
				black: '#000000',
				white: '#FFFFFF'
			},
			blue: {
				50: '#E9F6FD',
				100: '#C5E9FF',
				200: '#9DDDFF',
				300: '#73CFFF',
				400: '#4FC3FF',
				500: '#2BB8FF',
				600: '#21A9FF',
				700: '#1495ED',
				800: '#0884DC',
				900: '#0065BE'
			},
			cyan: {
				50: '#DBF1F5',
				100: '#A5DDE6',
				200: '#69C6D5',
				300: '#1AAFC4',
				400: '#00A1B8',
				500: '#0092AD',
				600: '#00849C',
				700: '#007184',
				800: '#005E6C',
				900: '#003D44'
			},
			green: {
				50: '#F2F7E6',
				100: '#DEEAC2',
				200: '#C9DC9D',
				300: '#B3CF76',
				400: '#A3C558',
				500: '#93BB3A',
				600: '#84AB32',
				700: '#6F9829',
				800: '#5B8420',
				900: '#38630F'
			},
			orange: {
				50: '#FAEBE7',
				100: '#FCD2BB',
				200: '#FCB58D',
				300: '#EC935D',
				400: '#F98535',
				500: '#F87300',
				600: '#ED6C00',
				700: '#DF6500',
				800: '#D25D00',
				900: '#A74B00'
			},
			rose: {
				50: '#FBEFF2',
				100: '#FDD5DD',
				200: '#F3A5AC',
				300: '#EF808B',
				400: '#FE616E',
				500: '#FF4F56',
				600: '#F84856',
				700: '#E53F4E',
				800: '#D83846',
				900: '#CA2C3A'
			}
		},
		typography: {
			fontFamily: 'Public sans, sans-serif',
			fontWeightLight: 400,
			fontWeightRegular: 500,
			fontWeightMedium: 600,
			fontWeightBold: 700,
			h1: {
				fontWeight: 700,
				fontSize: '2.375rem',
				lineHeight: 1.21
			},
			h2: {
				fontWeight: 700,
				fontSize: '1.875rem',
				lineHeight: 1.27
			},
			h3: {
				fontWeight: 700,
				fontSize: '1.675rem',
				lineHeight: 1.33
			},
			h4: {
				fontWeight: 700,
				fontSize: '1.5rem',
				lineHeight: 1.33
			},
			h5: {
				fontWeight: 500,
				fontSize: '1rem',
				lineHeight: 1.5
			},
			h6: {
				fontWeight: 500,
				fontSize: '0.875rem',
				lineHeight: 1.57
			},
			caption: {
				fontWeight: 400,
				fontSize: '0.75rem',
				lineHeight: 1.66
			},
			body1: {
				fontSize: '0.875rem',
				lineHeight: 1.57
			},
			body2: {
				fontSize: '0.75rem',
				lineHeight: 1.66
			},
			subtitle1: {
				fontSize: '0.875rem',
				fontWeight: 600,
				lineHeight: 1.57
			},
			subtitle2: {
				fontSize: '0.75rem',
				fontWeight: 500,
				lineHeight: 1.66
			},
			overline: {
				lineHeight: 1.66
			},
			button: {
				textTransform: 'initial'
			}
		},
		breakpoints: {
			values: {
				xs: 0,
				sm: 768,
				md: 1024,
				lg: 1266,
				xl: 1440
			}
		},
		components: {
			MuiCheckbox: {
				defaultProps: {
					icon: (
						<Box
							sx={ {
								width: 16,
								height: 16,
								border: '1px solid',
								borderColor: 'inherit',
								borderRadius: 0.25
							} }
						/>
					),
					checkedIcon: (
						<Box
							sx={ {
								width: 16,
								height: 16,
								border: '1px solid',
								borderColor: 'inherit',
								borderRadius: 0.25,
								position: 'relative'
							} }
						>
							<CheckSquareFilled />
						</Box>
					),
					indeterminateIcon: (
						<Box
							sx={ {
								width: 16,
								height: 16,
								border: '1px solid',
								borderColor: 'inherit',
								borderRadius: 0.25,
								position: 'relative'
							} }
						>
							<MinusSquareFilled />
						</Box>
					),
					size: 'small'
				},
				styleOverrides: {
					root: ( { theme } ) => ( {
						borderRadius: 0,
						padding: 0,
						color: theme.palette.secondary[ 300 ],
						'&.MuiCheckbox-sizeSmall': {
							'& > div': {
								width: 16,
								height: 16,
								'& > span': {
									fontSize: '1rem',
									top: -1,
									left: -1,
									position: 'absolute'
								}
							}
						},
						'&.MuiCheckbox-sizeMedium': {
							'& > div': {
								width: 20,
								height: 20,
								'& > span': {
									fontSize: '1.35rem',
									top: -2,
									left: -2,
									position: 'absolute'
								}
							}
						}
					} )
				}
			},
			MuiIconButton: {
				styleOverrides: {
					root: {
						borderRadius: 4
					},
					sizeLarge: ( { theme } ) => ( {
						width: theme.spacing( 5.5 ),
						height: theme.spacing( 5.5 ),
						fontSize: '1.25rem'
					} ),
					sizeMedium: ( { theme } ) => ( {
						width: theme.spacing( 4.5 ),
						height: theme.spacing( 4.5 ),
						fontSize: '1rem'
					} ),
					sizeSmall: ( { theme } ) => ( {
						width: theme.spacing( 3.75 ),
						height: theme.spacing( 3.75 ),
						fontSize: '0.75rem'
					} )
				}
			},
			MuiLink: {
				defaultProps: {
					underline: 'hover'
				}
			},
			MuiChip: {
				styleOverrides: {
					root: {
						borderRadius: 4,
						'&:active': {
							boxShadow: 'none'
						}
					}
				}
			},
			MuiDialogTitle: {
				styleOverrides: {
					root: {
						fontSize: '1rem',
						fontWeight: 700
					}
				}
			},
			MuiButton: {
				defaultProps: {
					disableElevation: true
				}
			},
			MuiSwitch: {
				styleOverrides: {
					track: ( { theme } ) => ( {
						opacity: 1,
						backgroundColor: theme.palette.secondary[ 400 ],
						boxSizing: 'border-box'
					} ),
					thumb: ( { theme } ) => ( {
						borderRadius: '50%',
						transition: theme.transitions.create( 'width', {
							easing: theme.transitions.easing.sharp,
							duration: theme.transitions.duration.standard
						} )
					} ),
					switchBase: ( { theme } ) => ( {
						'&.Mui-checked': {
							color: '#fff',
							'& + .MuiSwitch-track': {
								opacity: 1
							},
							'&.Mui-disabled': {
								color: theme.palette.background.paper
							}
						},
						'&.Mui-disabled': {
							color: theme.palette.background.paper,
							'+.MuiSwitch-track': {
								opacity: 0.3
							}
						}
					} ),
					root: ( { theme } ) => ( {
						color: theme.palette.text.primary,
						padding: 0,
						display: 'flex',
						width: theme.spacing( 5.5 ),
						height: theme.spacing( 2.75 ),
						'& .MuiSwitch-switchBase': {
							padding: theme.spacing( 0.375 ),
							'&.Mui-checked': {
								transform: `translateX(${ theme.spacing( 2.75 ) })`
							}
						},
						'& .MuiSwitch-thumb': {
							width: theme.spacing( 2 ),
							height: theme.spacing( 2 )
						},
						'& .MuiSwitch-track': {
							borderRadius: theme.spacing( 2 )
						}
					} )
				}
			},
			MuiTableRow: {
				styleOverrides: {
					root: ( { theme } ) => ( {
						'&:last-of-type': {
							'& .MuiTableCell-root': {
								borderBottom: 'none'
							}
						},
						'& .MuiTableCell-root': {
							'&:last-of-type': {
								paddingRight: theme.spacing( 3 )
							},
							'&:first-of-type': {
								paddingLeft: theme.spacing( 3 )
							}
						}
					} )
				}
			},
			MuiTableCell: {
				styleOverrides: {
					root: ( { theme } ) => ( {
						fontSize: '0.875rem',
						padding: `${ theme.spacing( 1 ) } ${ theme.spacing( 2 ) }`,
						borderColor: theme.palette.secondary[ 100 ],
						'.MuiTableRow-root &:first-of-type': {
							paddingLeft: theme.spacing( 2 )
						}
					} ),
					head: ( { theme } ) => ( {
						backgroundColor: theme.palette.grey[ 200 ],
						padding: `${ theme.spacing( 3 ) } ${ theme.spacing( 2 ) }`,
						'&:not(:last-of-type)': {
							position: 'relative',
							'&:after': {
								position: 'absolute',
								content: '""',
								backgroundColor: theme.palette.grey[ 300 ],
								width: 1,
								height: 'calc(100% - 30px)',
								right: 0,
								top: theme.spacing( 2 )
							}
						}
					} )
				}
			},

			MuiAlert: {
				defaultProps: {
					severity: 'info'
				},
				styleOverrides: {
					root: ( { theme } ) => ( {
						fontSize: 14,
						fontWeight: theme.typography.fontWeightLight,

						'&.MuiAlert-outlined': {
							border: '1px solid',
							color: theme.palette.secondary[ 800 ],
							padding: '9px 16px'
						},

						'&.MuiAlert-outlinedError': {
							borderColor: theme.palette.error[ 100 ],

							'& .MuiAlert-icon': {
								color: theme.palette.error[ 400 ]
							}
						},

						'&.MuiAlert-outlinedInfo': {
							borderColor: theme.palette.info.light,

							'& .MuiAlert-icon': {
								color: theme.palette.info[ 400 ]
							}
						},

						'&.MuiAlert-outlinedSuccess': {
							borderColor: theme.palette.success.light,

							'& .MuiAlert-icon': {
								color: theme.palette.success[ 400 ]
							}
						},

						'&.MuiAlert-outlinedWarning': {
							borderColor: theme.palette.warning.light,

							'& .MuiAlert-icon': {
								color: theme.palette.warning[ 400 ]
							}
						},

						'&.MuiAlert-standard': {
							border: '1px solid',
							color: theme.palette.secondary[ 800 ],
							padding: '9px 16px'
						},

						'&.MuiAlert-standardError': {
							background: theme.palette.error[ 50 ],
							borderColor: theme.palette.error[ 100 ],

							'& .MuiAlert-icon': {
								color: theme.palette.error[ 400 ]
							}
						},

						'&.MuiAlert-standardInfo': {
							background: theme.palette.info[ 100 ],
							borderColor: theme.palette.info.light,

							'& .MuiAlert-icon': {
								color: theme.palette.info[ 400 ]
							}
						},

						'&.MuiAlert-standardSuccess': {
							background: theme.palette.success[ 100 ],
							borderColor: theme.palette.success.light,

							'& .MuiAlert-icon': {
								color: theme.palette.success[ 400 ]
							}
						},

						'&.MuiAlert-standardWarning': {
							background: theme.palette.warning[ 100 ],
							borderColor: theme.palette.warning.light,

							'& .MuiAlert-icon': {
								color: theme.palette.warning[ 400 ]
							}
						},

						'& .MuiAlert-icon': {
							margin: 'initial',
							padding: 'initial',

							marginRight: 10
						},

						'& .MuiAlert-message': {
							padding: 'initial'
						}
					} )
				}
			},
			MuiAlertTitle: {
				styleOverrides: {
					root: ( { theme } ) => ( {
						margin: 'initial',

						fontSize: 16,
						fontWeight: theme.typography.fontWeightLight,
						marginBottom: 6
					} )
				}
			},
			MuiButtonBase: {
				defaultProps: {
					disableRipple: true
				}
			},
			MuiFormControl: {
				defaultProps: {
					fullWidth: true
				}
			},
			MuiFormHelperText: {
				styleOverrides: {
					root: ( { theme } ) => ( {
						margin: 'initial',

						color: theme.palette.secondary.dark,
						fontSize: 14,
						fontWeight: theme.typography.fontWeightLight,

						'&.Mui-disabled': {
							color: theme.palette.secondary.light
						},

						'&.Mui-error': {
							color: theme.palette.error.main
						}
					} )
				}
			},
			MuiInputLabel: {
				defaultProps: {
					shrink: true
				},
				styleOverrides: {
					root: ( { theme } ) => ( {
						maxWidth: 'initial',
						pointerEvents: 'initial',
						position: 'initial',
						transform: 'initial',

						color: theme.palette.secondary.dark,
						// display: 'flex',
						fontSize: 14,
						fontWeight: theme.typography.fontWeightLight,
						// gap: 4,
						marginBottom: 8,

						'&.Mui-focused': {
							color: theme.palette.secondary.dark
						},

						'&.Mui-disabled': {
							color: theme.palette.secondary.light
						},

						'&.Mui-error': {
							color: theme.palette.secondary.dark
						},

						'& .MuiInputLabel-asterisk': {
							display: 'none'

							// color: theme.palette.error.main,
							// order: -1,
						}
					} )
				}
			},
			MuiList: {
				defaultProps: {
					disablePadding: true
				}
			},
			MuiListItem: {
				defaultProps: {
					disablePadding: true
				},
				styleOverrides: {
					divider: ( { theme } ) => ( {
						borderBottom: 'initial',

						borderTop: `1px solid ${ theme.palette.grey[ 500 ] }`
					} )
				}
			},
			MuiListItemButton: {
				styleOverrides: {
					root: ( { theme } ) => ( {
						gap: 8,

						'&.Mui-selected': {
							color: theme.palette.primary.main
						}
					} )
				}
			},
			MuiListItemIcon: {
				styleOverrides: {
					root: {
						minWidth: 'initial',

						color: 'inherit'
					}
				}
			},
			MuiOutlinedInput: {
				defaultProps: {
					notched: false
				},
				styleOverrides: {
					root: ( { theme } ) => ( {
						color: theme.palette.secondary.dark,

						'&:hover': {
							'& .MuiOutlinedInput-notchedOutline': {
								borderColor: theme.palette.secondary.dark
							}
						},

						'&.Mui-focused': {
							'& .MuiOutlinedInput-notchedOutline': {
								borderColor: theme.palette.primary.main
							}
						},

						'&.Mui-disabled': {
							color: theme.palette.secondary.light,

							'& .MuiOutlinedInput-input': {
								'&::placeholder': {
									color: 'currentColor'
								}
							},

							'& .MuiOutlinedInput-notchedOutline': {
								borderColor: theme.palette.secondary[ 100 ]
							}
						},

						'&.Mui-error': {
							'& .MuiOutlinedInput-notchedOutline': {
								borderColor: theme.palette.error[ 400 ]
							}
						},

						'& .MuiOutlinedInput-input': {
							fontSize: 14,
							fontWeight: theme.typography.fontWeightLight,
							padding: '8px 12px',

							'&::placeholder': {
								opacity: 'initial',

								color: theme.palette.secondary[ 400 ]
							}
						},

						'& .MuiOutlinedInput-notchedOutline': {
							borderColor: theme.palette.secondary[ 100 ]
						}
					} )
				}
			},
			MuiPaper: {
				defaultProps: {
					variant: 'outlined'
				}
			},
			MuiSelect: {
				defaultProps: {
					displayEmpty: true
				},
				styleOverrides: {
					icon: ( { theme } ) => ( {
						color: theme.palette.secondary[ 400 ]
					} )
				}
			},
			MuiTab: {
				defaultProps: {
					iconPosition: 'start'
				},
				styleOverrides: {
					root: {
						minHeight: 46
					}
				}
			},
			MuiTabs: {
				defaultProps: {
					variant: 'fullWidth'
				},
				styleOverrides: {
					root: {
						minHeight: 'initial'
					}
				}
			},
			MuiTextField: {
				defaultProps: {
					fullWidth: true,
					InputProps: {
						label: null
					}
				}
			}
		}
	} );

	return theme;
};
